"use client";

import { useRef, useCallback, useState, useLayoutEffect } from "react";
import { debounce } from "../delay";

type TScrollDims = {
  top: number;
  left: number;
};

type TScrollDimsWithUpScroll = {
  top: number;
  left: number;
  hasScrolledUp: boolean;
};

const initialDims: TScrollDims = {
  top: 0,
  left: 0
};

const initialDimsWithScrollUp: TScrollDimsWithUpScroll = {
  top: 0,
  left: 0,
  hasScrolledUp: false
};

// only used for ToolTop
export const useElementScroll = (elementId: string): TScrollDims => {
  const [elementScroll, setElementScroll] = useState<TScrollDims>(initialDims);

  const handleScroll = useCallback((el: HTMLElement) => {
    setElementScroll({
      left: el.scrollLeft,
      top: el.scrollTop
    });
  }, []);

  useLayoutEffect(() => {
    const el: HTMLElement | null = document.getElementById(elementId);
    if (el === null) return undefined;

    handleScroll(el);

    // Debounce to avoid the function fire multiple times
    const handleResizeDebounced = debounce(() => handleScroll(el), 25);
    el.addEventListener("scroll", handleResizeDebounced);

    return () => {
      el.removeEventListener("scroll", handleResizeDebounced);
    };
  }, [elementId, handleScroll]);

  return elementScroll;
};

// only used in HeaderContext
export const useWindowScroll = (isMobile: boolean): TScrollDimsWithUpScroll => {
  const [elementScroll, setElementScroll] = useState<TScrollDimsWithUpScroll>(
    initialDimsWithScrollUp
  );
  const lastScrollYPos = useRef(0);

  const handleScroll = useCallback(() => {
    setElementScroll({
      left: window.scrollX,
      top: window.scrollY,
      // has user scrolled up?
      hasScrolledUp:
        window.scrollY < lastScrollYPos.current || window.scrollY === 0
          ? true
          : false
    });

    lastScrollYPos.current = window.scrollY;
  }, []);

  useLayoutEffect(() => {
    handleScroll();

    // Debounce to avoid the function fire multiple times
    const handleResizeDebounced = debounce(() => handleScroll(), 25);
    document.addEventListener("scroll", handleResizeDebounced);

    return () => {
      document.removeEventListener("scroll", handleResizeDebounced);
    };
  }, [handleScroll, isMobile]);

  return elementScroll;
};
